/**
 * Organization and project route constants.
 *
 * Contains organization routes (list, details, edit, members, settings,
 * vcs connections, billing) and project routes (nested under organizations),
 * plus helper functions for generating parameterized paths.
 */

import { PREFIXES, buildPath, RouteWithPath } from "./routes-platform";

// Organization route types to avoid circular references
export interface OrganizationDetailRoute extends RouteWithPath {
  getPath: (orgAlias: string) => string;
}

export interface OrganizationEditRoute extends RouteWithPath {
  getPath: (orgAlias: string) => string;
}

export interface OrganizationSubRoute extends RouteWithPath {
  getPath: (orgAlias: string) => string;
}

export interface OrganizationRoutes {
  ROOT: RouteWithPath;
  LIST: RouteWithPath;
  DETAILS: OrganizationDetailRoute;
  CREATE: RouteWithPath;
  NEW: RouteWithPath;
  EDIT: OrganizationEditRoute;
  MEMBERS: OrganizationSubRoute;
  ORG_SETTINGS: OrganizationSubRoute;
  VCS_CONNECTIONS: OrganizationSubRoute;
  BILLING: OrganizationSubRoute;
  FINOPS: OrganizationSubRoute;
  getPath: (route: "ROOT" | "LIST" | "CREATE" | "NEW") => string;
}

export interface PlatformProductsCatalogRoute extends RouteWithPath {
  getPath: (orgAlias: string) => string;
}

export interface PlatformProductDetailsRoute extends RouteWithPath {
  getPath: (orgAlias: string, productKey: string) => string;
}

export const PLATFORM_PRODUCTS = {
  CATALOG: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/platform-products`,
    title: "Platform Products",
    getPath: (orgAlias: string) =>
      buildPath(`${PREFIXES.ORGANIZATIONS}/:orgAlias/platform-products`, {
        orgAlias,
      }),
  } satisfies PlatformProductsCatalogRoute,
  DETAILS: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/platform-products/:productKey`,
    title: "Product Details",
    getPath: (orgAlias: string, productKey: string) =>
      buildPath(
        `${PREFIXES.ORGANIZATIONS}/:orgAlias/platform-products/:productKey`,
        { orgAlias, productKey },
      ),
  } satisfies PlatformProductDetailsRoute,
};

// Organization routes with metadata
export const ORGANIZATIONS: OrganizationRoutes = {
  ROOT: {
    path: PREFIXES.ORGANIZATIONS,
    title: "Organizations",
  },
  LIST: {
    path: PREFIXES.ORGANIZATIONS,
    title: "All Organizations",
  },
  DETAILS: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias`,
    title: "Organization Details",

    // Helper function to generate path with params
    getPath: (orgAlias: string) =>
      buildPath(`${PREFIXES.ORGANIZATIONS}/:orgAlias`, { orgAlias }),
  },
  CREATE: {
    path: `${PREFIXES.ORGANIZATIONS}/create`,
    title: "Create Organization",
  },
  NEW: {
    path: `${PREFIXES.ORGANIZATIONS}/new`,
    title: "New Organization",
  },
  EDIT: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/edit`,
    title: "Edit Organization",

    // Helper function to generate path with params
    getPath: (orgAlias: string) =>
      buildPath(`${PREFIXES.ORGANIZATIONS}/:orgAlias/edit`, { orgAlias }),
  },
  MEMBERS: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/members`,
    title: "Organization Members",

    getPath: (orgAlias: string) =>
      buildPath(`${PREFIXES.ORGANIZATIONS}/:orgAlias/members`, { orgAlias }),
  },
  ORG_SETTINGS: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/settings`,
    title: "Organization Settings",

    getPath: (orgAlias: string) =>
      buildPath(`${PREFIXES.ORGANIZATIONS}/:orgAlias/settings`, { orgAlias }),
  },
  VCS_CONNECTIONS: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/connections`,
    title: "Connections",

    getPath: (orgAlias: string) =>
      buildPath(`${PREFIXES.ORGANIZATIONS}/:orgAlias/connections`, {
        orgAlias,
      }),
  },
  BILLING: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/billing`,
    title: "Billing",

    getPath: (orgAlias: string) =>
      buildPath(`${PREFIXES.ORGANIZATIONS}/:orgAlias/billing`, {
        orgAlias,
      }),
  },
  FINOPS: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/finops`,
    title: "FinOps",

    getPath: (orgAlias: string) =>
      buildPath(`${PREFIXES.ORGANIZATIONS}/:orgAlias/finops`, {
        orgAlias,
      }),
  },

  // Helper function to generate paths with params for any org route
  getPath: (route: "ROOT" | "LIST" | "CREATE" | "NEW"): string =>
    ORGANIZATIONS[route].path,
};

// Project route types
export interface ProjectRootRoute extends RouteWithPath {
  getPath: (orgAlias: string) => string;
}

export interface ProjectListRoute extends RouteWithPath {
  getPath: (orgAlias: string) => string;
}

export interface ProjectDetailsRoute extends RouteWithPath {
  getPath: (orgAlias: string, projectAlias: string) => string;
}

export interface ProjectCreateRoute extends RouteWithPath {
  getPath: (orgAlias: string) => string;
}

export interface ProjectEditRoute extends RouteWithPath {
  getPath: (orgAlias: string, projectAlias: string) => string;
}

export interface ProvisionResourceRoute extends RouteWithPath {
  getPath: (
    orgAlias: string,
    projectAlias: string,
    resourceType: string,
  ) => string;
}

export interface LegacyProjectDetailsRoute extends RouteWithPath {
  getPath: (projectAlias: string) => string;
}

export interface LegacyProjectEditRoute extends RouteWithPath {
  getPath: (projectAlias: string) => string;
}

export interface LegacyProjectRoutes {
  ROOT: RouteWithPath;
  DETAILS: LegacyProjectDetailsRoute;
  EDIT: LegacyProjectEditRoute;
}

export interface ResourceDetailRoute extends RouteWithPath {
  getPath: (
    orgAlias: string,
    projectAlias: string,
    resourceAlias: string,
  ) => string;
}

export interface ProjectRoutes {
  ROOT: ProjectRootRoute;
  LIST: ProjectListRoute;
  DETAILS: ProjectDetailsRoute;
  CREATE: ProjectCreateRoute;
  EDIT: ProjectEditRoute;
  RESOURCES_NEW: ProvisionResourceRoute;
  RESOURCES_DETAIL: ResourceDetailRoute;
}

// Project routes with metadata
export const PROJECTS: ProjectRoutes = {
  // Organization-nested project routes
  ROOT: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects`,
    title: "Projects",

    // Helper function to generate path with params
    getPath: (orgAlias: string) =>
      buildPath(`${PREFIXES.ORGANIZATIONS}/:orgAlias/projects`, { orgAlias }),
  },
  LIST: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects`,
    title: "All Projects",

    // Helper function to generate path with params
    getPath: (orgAlias: string) =>
      buildPath(`${PREFIXES.ORGANIZATIONS}/:orgAlias/projects`, { orgAlias }),
  },
  DETAILS: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias`,
    title: "Project Details",

    // Helper function to generate path with params
    getPath: (orgAlias: string, projectAlias: string) =>
      buildPath(`${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias`, {
        orgAlias,
        projectAlias,
      }),
  },
  CREATE: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/create`,
    title: "Create Project",

    // Helper function to generate path with params
    getPath: (orgAlias: string) =>
      buildPath(`${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/create`, {
        orgAlias,
      }),
  },
  EDIT: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias/edit`,
    title: "Edit Project",

    // Helper function to generate path with params
    getPath: (orgAlias: string, projectAlias: string) =>
      buildPath(
        `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias/edit`,
        { orgAlias, projectAlias },
      ),
  },
  RESOURCES_NEW: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias/resources/new/:resourceType`,
    title: "Provision Resource",

    getPath: (orgAlias: string, projectAlias: string, resourceType: string) =>
      buildPath(
        `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias/resources/new/:resourceType`,
        { orgAlias, projectAlias, resourceType },
      ),
  },
  RESOURCES_DETAIL: {
    path: `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias/resources/:resourceAlias`,
    title: "Resource Details",

    getPath: (orgAlias: string, projectAlias: string, resourceAlias: string) =>
      buildPath(
        `${PREFIXES.ORGANIZATIONS}/:orgAlias/projects/:projectAlias/resources/:resourceAlias`,
        { orgAlias, projectAlias, resourceAlias },
      ),
  },
};

// Helper functions for route generation
export const getOrganizationDetailsPath = (alias: string) =>
  ORGANIZATIONS.DETAILS.getPath(alias);
export const getOrganizationEditPath = (alias: string) =>
  ORGANIZATIONS.EDIT.getPath(alias);
export const getOrganizationMembersPath = (alias: string) =>
  ORGANIZATIONS.MEMBERS.getPath(alias);
export const getOrganizationSettingsPath = (alias: string) =>
  ORGANIZATIONS.ORG_SETTINGS.getPath(alias);
export const getProjectsRootPath = (orgAlias: string) =>
  PROJECTS.ROOT.getPath(orgAlias);
export const getProjectsListPath = (orgAlias: string) =>
  PROJECTS.LIST.getPath(orgAlias);
export const getProjectDetailsPath = (orgAlias: string, projectAlias: string) =>
  PROJECTS.DETAILS.getPath(orgAlias, projectAlias);
export const getProjectCreatePath = (orgAlias: string) =>
  PROJECTS.CREATE.getPath(orgAlias);
export const getProjectEditPath = (orgAlias: string, projectAlias: string) =>
  PROJECTS.EDIT.getPath(orgAlias, projectAlias);
